import classNames from 'classnames'
import { useMemo } from 'react'
import { PrivacyState, EffectivePrivacy } from '@opallabs/concepts'
import { pluralize } from '@opallabs/lambda'
import {
  HeadingSize,
  Icon,
  ParagraphText,
  SectionHeading,
} from '@opallabs/lapidary'
import { AccessSummaryState } from '../types'
import * as styles from './styles'

export type PermissionsTextSummaryProps = {
  className?: string
  effectivePrivacy: EffectivePrivacy | null
  privacyState: PrivacyState
  memberCount: number
  /**
   * The `type` of resource being accessed. This may be a custom type
   * (for blocks) or a user-facing name that is different
   * from the BE resource type (e.g. "plan")
   */
  resourceDisplayType: string
  spaceCount: number
}

/**
 * AccessSummaryState.Members is handled separate because
 * it require interpolation of members and spaces counts
 */
export const accessTitles: Record<
  Exclude<AccessSummaryState, AccessSummaryState.SpecificCollaborators>,
  string
> = {
  [AccessSummaryState.InheritedPublic]: 'Everyone in this workspace',
  [AccessSummaryState.Public]: 'Everyone in this workspace',
  [AccessSummaryState.OnlyMe]: 'Only me',
}

const memberCountText = (memberCount: number) =>
  `${memberCount} ${memberCount > 1 ? 'people' : 'person'}`
const spacesCountText = (spaceCount: number) => {
  if (!spaceCount) return ''

  return ` and ${spaceCount} ${pluralize('space', spaceCount)}`
}

export const accessDescriptions: Record<
  AccessSummaryState,
  (resourceName: string) => string
> = {
  [AccessSummaryState.InheritedPublic]: (resourceName: string) =>
    `Everyone in this workspace can find, view and edit this ${resourceName} because one or more of its parent spaces are set to "Inherit Access", and are open to everyone in the workspace.`,
  [AccessSummaryState.Public]: (resourceName: string) =>
    `Everyone in this workspace can find, view and edit this ${resourceName}, regardless of the permissions of its parent spaces.`,
  [AccessSummaryState.SpecificCollaborators]: (resourceName: string) =>
    `Only the specific people & spaces given access can find, view and edit this ${resourceName}.`,
  [AccessSummaryState.OnlyMe]: (resourceName: string) =>
    `Only I can find, view and edit this ${resourceName}.`,
}

const getAccessSummaryState = ({
  effectivePrivacy,
  privacyState,
  memberCount,
  spaceCount,
}: Omit<
  PermissionsTextSummaryProps,
  'className' | 'resourceDisplayType'
>): AccessSummaryState => {
  // This privacy state is set only when membership is explicitly inherited from workspace.
  // All other access summary states assume privacyState is PrivacyState.Private
  if (privacyState === PrivacyState.Public) return AccessSummaryState.Public

  if (effectivePrivacy === EffectivePrivacy.EffectivelyPublic)
    return AccessSummaryState.InheritedPublic

  if (memberCount > 1 || spaceCount > 0)
    return AccessSummaryState.SpecificCollaborators

  return AccessSummaryState.OnlyMe
}

export const PermissionsTextSummary: React.FC<PermissionsTextSummaryProps> = ({
  className,
  effectivePrivacy,
  memberCount,
  privacyState,
  resourceDisplayType,
  spaceCount,
}) => {
  const accessSummaryState = getAccessSummaryState({
    effectivePrivacy,
    privacyState,
    memberCount,
    spaceCount,
  })

  const { title, description } = useMemo(() => {
    if (accessSummaryState === AccessSummaryState.SpecificCollaborators) {
      return {
        title: `${memberCountText(memberCount)}${spacesCountText(spaceCount)}`,
        description:
          accessDescriptions[accessSummaryState](resourceDisplayType),
      }
    }

    return {
      title: accessTitles[accessSummaryState],
      description: accessDescriptions[accessSummaryState](resourceDisplayType),
    }
  }, [accessSummaryState, memberCount, spaceCount, resourceDisplayType])

  const isPrivate =
    accessSummaryState === AccessSummaryState.SpecificCollaborators ||
    accessSummaryState === AccessSummaryState.OnlyMe

  return (
    <div
      className={classNames(styles.classes.root, className)}
      css={styles.privacySummary}
    >
      <SectionHeading
        css={styles.privacySummaryTitle}
        level={3}
        size={HeadingSize.small}
      >
        <Icon
          width={24}
          height={24}
          glyph={`gem-${isPrivate ? 'locked' : 'globe'}`}
        />
        {title}
      </SectionHeading>
      <ParagraphText css={styles.privacyDescription}>
        {description}
      </ParagraphText>
    </div>
  )
}
